import { Link, useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Sparkles, Instagram, Mail, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import NewsletterForm from './NewsletterForm';

export default function PublicLayout({ children }) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get('/public/zones').then((r) => setZones(r.data?.zones || [])).catch(() => {});
    api.get('/public/settings').then((r) => setSettings(r.data || {})).catch(() => {});
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const toggleLang = () => {
    const next = i18n.language?.startsWith('en') ? 'it' : 'en';
    i18n.changeLanguage(next);
  };

  return (
    <div className="min-h-screen flex flex-col text-[color:var(--text)]" data-testid="public-layout">
      <header className="sticky top-0 z-40 border-b border-[color:var(--border-c)] bg-[rgba(11,11,12,0.92)] backdrop-blur supports-[backdrop-filter]:bg-[rgba(11,11,12,0.72)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo-link">
            <Sparkles className="w-5 h-5 text-[color:var(--gold)] group-hover:text-[color:var(--gold-soft)] transition-colors" />
            <span className="font-heading text-xl tracking-tight text-[color:var(--text)]">Roma <span className="text-[color:var(--gold)]">Nightlife</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <NavLink to="/locali" className={({isActive}) => `transition-colors ${isActive ? 'text-[color:var(--gold)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text)]'}`} data-testid="nav-locali-link">{t('nav.locali')}</NavLink>
            <div className="relative group">
              <button className="text-[color:var(--text-muted)] hover:text-[color:var(--text)] transition-colors" data-testid="nav-zone-btn">{t('nav.zone')}</button>
              <div className="absolute right-0 top-full pt-3 hidden group-hover:block">
                <div className="min-w-[220px] rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-2 shadow-2xl grid grid-cols-2 gap-1">
                  {zones.map((z) => (
                    <Link key={z} to={`/serate-roma-${z.toLowerCase().replace(/\s+/g,'-')}`} className="px-3 py-2 text-sm rounded-md hover:bg-[rgba(214,185,140,0.08)] hover:text-[color:var(--gold)]">{z}</Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={toggleLang} data-testid="language-toggle" className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] hover:text-[color:var(--gold)]">
              <Globe className="w-4 h-4 mr-1" /> {i18n.language?.startsWith('en') ? 'EN' : 'IT'}
            </Button>
            <Link to="/admin/login" className="hidden sm:inline-flex text-xs text-[color:var(--text-subtle)] hover:text-[color:var(--gold)] transition-colors" data-testid="nav-admin-link">{t('nav.admin')}</Link>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-16 border-t border-[color:var(--border-c)] bg-[color:var(--bg-2)]" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[color:var(--gold)]" />
              <span className="font-heading text-lg">Roma Nightlife</span>
            </div>
            <p className="text-sm text-[color:var(--text-muted)] mb-4">{t('footer.about')}</p>
            <h4 className="font-heading text-sm mb-2 text-[color:var(--gold)] uppercase tracking-wider">{t('footer.newsletterTitle')}</h4>
            <p className="text-xs text-[color:var(--text-muted)] mb-3">{t('newsletter.text')}</p>
            <NewsletterForm source="footer" />
          </div>
          <div className="md:col-span-3">
            <h4 className="font-heading text-sm mb-3 text-[color:var(--gold)] uppercase tracking-wider">{t('footer.zonesTitle')}</h4>
            <ul className="space-y-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              {zones.map((z) => (
                <li key={z}><Link to={`/serate-roma-${z.toLowerCase().replace(/\s+/g,'-')}`} data-testid={`footer-zone-${z.toLowerCase().replace(/\s+/g,'-')}`} className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--gold)]">Serate a {z}</Link></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-heading text-sm mb-3 text-[color:var(--gold)] uppercase tracking-wider">{t('footer.infoTitle')}</h4>
            <ul className="space-y-1">
              <li><Link to="/locali" className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--gold)]">{t('nav.locali')}</Link></li>
              <li><Link to="/collabora" className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--gold)] inline-flex items-center gap-1" data-testid="nav-collabora-footer"><Handshake className="w-3 h-3"/> {t('collabora.navLink')}</Link></li>
              <li><Link to="/admin/login" className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--gold)]">{t('nav.admin')}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-heading text-sm mb-3 text-[color:var(--gold)] uppercase tracking-wider">{t('footer.contact')}</h4>
            <ul className="space-y-2 text-sm text-[color:var(--text-muted)]">
              {settings.contact_email && (<li className="flex items-center gap-2"><Mail className="w-4 h-4"/><a href={`mailto:${settings.contact_email}`} className="hover:text-[color:var(--gold)]">{settings.contact_email}</a></li>)}
              {settings.instagram_url && (<li className="flex items-center gap-2"><Instagram className="w-4 h-4"/><a href={settings.instagram_url} target="_blank" rel="noreferrer" className="hover:text-[color:var(--gold)]">Instagram</a></li>)}
            </ul>
          </div>
        </div>
        <div className="gold-divider"/>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs text-[color:var(--text-subtle)] flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} Roma Nightlife. {t('footer.rights')}</span>
          <span className="hidden sm:inline">Made with care in Roma</span>
        </div>
      </footer>
    </div>
  );
}
